import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useApplicationState } from '@atlas/core';

import { POSTHOG_EVENTS } from 'common/constants';

import usePostHog from './usePostHog';

const useBonusActionPostHog = ({ trackPageLeave = false } = {}) => {
  const postHog = usePostHog();
  const { pathname } = useLocation();

  const { account, wallet: { detailedBalance: { BONUS = 0, CASH = 0, FROZEN = 0 } = {} } = {} } =
    useApplicationState() || {};

  const clientId = account?.id ?? null;

  const getBaseData = useCallback(
    () => ({
      action_page: pathname,
      client_id: clientId,
      timestamp: Date.now(),
      user_bonus_balance: BONUS,
      user_cash_balance: CASH,
      user_frozen_balance: FROZEN,
    }),
    [clientId, pathname, BONUS, CASH, FROZEN],
  );

  const getBaseDataRef = useRef(getBaseData);
  getBaseDataRef.current = getBaseData;

  const postHogRef = useRef(postHog);
  postHogRef.current = postHog;

  const handleBonusCta = useCallback(
    ({ bonusId = null, bonusInstanceId = null, ctaClicked }) => {
      postHog.send(POSTHOG_EVENTS.BONUS_ACTION, {
        ...getBaseData(),
        bonus_id: bonusId,
        bonus_instance_id: bonusInstanceId,
        cta_clicked: ctaClicked,
      });
    },
    [getBaseData, postHog],
  );

  useEffect(() => {
    if (!trackPageLeave) return undefined;

    return () => {
      postHogRef.current.send(POSTHOG_EVENTS.BONUS_ACTION, {
        ...getBaseDataRef.current(),
        bonus_id: null,
        bonus_instance_id: null,
        cta_clicked: null,
      });
    };
  }, [trackPageLeave]);

  return { handleBonusCta };
};

export default useBonusActionPostHog;
